import React from "react";
import { Paper, Box } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { FiltersState } from "./use-task-info-filters";
import DateRangePicker from "./date-range-picker";
import TableSelect from "./table-select";
import StatusSelect from "./status-select";
import QuickRangeButtons from "./quick-range-buttons";
import AutoUpdateIndicator from "./auto-update-indicator";
import ApplyFiltersButton from "./apply-filters-button";

interface TaskInfoFiltersProps {
  filters: FiltersState;
  onFilterChange: (key: string, value: unknown) => void;
  onClick: () => void;
  isAutoUpdating?: boolean;
}

const TaskInfoFilters: React.FC<TaskInfoFiltersProps> = ({
  filters,
  onFilterChange,
  onClick,
  isAutoUpdating = false,
}) => {
  const handleOnRetrieve = () => {
    if (!filters.activeTable) return;
    onClick();
  };

  const handleRangeChange = (dateFrom: string, dateTo: string) => {
    onFilterChange("dateFrom", dateFrom);
    onFilterChange("dateTo", dateTo);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        border: "1px solid",
        borderColor: "grey.200",
        borderRadius: 3,
        background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
        boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        },
        mb: 4,
      }}
    >
      <Grid container spacing={2.5} alignItems="flex-end">
        <Grid size={{ xs: 12 }}>
          <QuickRangeButtons
            dateFrom={filters.dateFrom}
            dateTo={filters.dateTo}
            onRangeChange={handleRangeChange}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <DateRangePicker
              dateFrom={filters.dateFrom}
              dateTo={filters.dateTo}
              onChange={onFilterChange}
            />
          </Box>
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <TableSelect
            value={filters.activeTable || ""}
            onChange={(value) => onFilterChange("activeTable", value)}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <StatusSelect
            selectedStatuses={filters.activeStatuses || []}
            onChange={(statuses) => onFilterChange("activeStatuses", statuses)}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <AutoUpdateIndicator isAutoUpdating={isAutoUpdating} />
            <ApplyFiltersButton
              disabled={!filters.activeTable}
              onClick={handleOnRetrieve}
            />
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default TaskInfoFilters;
